import React, { useState } from "react";
import { X, ArrowRight, Check, Activity, AlertTriangle } from "lucide-react";
import {
  ASSESSMENT_CONFIG,
  calculateResult,
  RESULTS,
} from "../data/assessmentData";

export default function AssessmentModal({ open, onClose }) {
  if (!open) return null;

  const [step, setStep] = useState(0); // 0 to N questions
  const [answers, setAnswers] = useState({});
  const [finished, setFinished] = useState(false);

  // Flatten questions into a linear array for the wizard
  const allQuestions = ASSESSMENT_CONFIG.sections.flatMap((sec) =>
    sec.questions.map((q) => ({ ...q, sectionTitle: sec.title }))
  );

  const currentQ = allQuestions[step];
  const progress = ((step + 1) / allQuestions.length) * 100;

  const handleSelect = (option) => {
    // Record answer
    const newAnswers = { ...answers, [currentQ.id]: option };
    setAnswers(newAnswers);

    // Next step or Finish
    if (step < allQuestions.length - 1) {
      setTimeout(() => setStep(step + 1), 250); // Slight delay for UX
    } else {
      setFinished(true);
    }
  };

  // --- RESULT VIEW ---
  if (finished) {
    const resultKey = calculateResult(answers);
    const result = RESULTS[resultKey];

    return (
      <div style={styles.overlay}>
        <div style={styles.container}>
          <div style={{ padding: 40, textAlign: "center" }}>
            <div
              style={{
                ...styles.iconBadge,
                color: result.color,
                borderColor: result.color,
              }}
            >
              <Activity size={32} />
            </div>

            <div
              style={{
                marginTop: 24,
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "var(--muted)",
              }}
            >
              DIAGNOSTIC COMPLETE
            </div>

            <h2 style={{ fontSize: 36, margin: "16px 0", color: "#fff" }}>
              {result.title}
            </h2>

            <div
              style={{ fontSize: 18, color: result.color, marginBottom: 24 }}
            >
              {result.summary}
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.05)",
                padding: 24,
                borderRadius: 16,
                textAlign: "left",
                marginBottom: 32,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                The Prescription
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: "#e2e8f0",
                }}
              >
                {result.prescription}
              </p>
            </div>

            <button
              onClick={onClose}
              className="btn primary"
              style={{ width: "100%", justifyContent: "center" }}
            >
              Accept Plan & Book Call
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- QUESTION WIZARD VIEW ---
  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: "var(--muted)",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            {currentQ.sectionTitle} // SYSTEM CHECK
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div
          style={{
            height: 4,
            background: "rgba(255,255,255,0.1)",
            width: "100%",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "var(--brand)",
              transition: "width 0.3s ease",
            }}
          />
        </div>

        {/* Question Body */}
        <div style={{ padding: "40px 32px" }}>
          <h3 style={{ fontSize: 24, margin: "0 0 32px 0", color: "#fff" }}>
            {currentQ.label}
          </h3>

          <div style={{ display: "grid", gap: 12 }}>
            {currentQ.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleSelect(opt)}
                style={styles.optionBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                  e.currentTarget.style.borderColor = "var(--brand)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                }}
              >
                {opt.label}
                {answers[currentQ.id] === opt && (
                  <Check size={18} color="var(--brand)" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={{ fontSize: 12, color: "var(--muted)" }}>
            {step + 1} / {allQuestions.length}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    backgroundColor: "rgba(0,0,0,0.9)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  container: {
    width: "100%",
    maxWidth: 500,
    background: "#0b0c10",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
    animation: "fadeIn 0.2s ease-out",
  },
  header: {
    padding: "20px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "var(--muted)",
    cursor: "pointer",
  },
  optionBtn: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "16px 20px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    color: "#e2e8f0",
    fontSize: 16,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  footer: {
    padding: "16px 32px",
    borderTop: "1px solid rgba(255,255,255,0.05)",
    textAlign: "right",
  },
  iconBadge: {
    width: 64,
    height: 64,
    margin: "0 auto",
    borderRadius: "50%",
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,0.2)",
  },
};
